/**
 * components/hire/updateResumeModal.tsx
 * "Update resume" — the sanctioned way to edit a candidate's matching data.
 * Candidate PII (name/email/phone) is immutable by design (DPDP/append-only DB
 * guard), so editing = appending a new resume version, which re-extracts the
 * profile (location, experience, skills, title). Uses POST /candidates/:id/resumes.
 *
 * Confirm before writing + success after — both as in-modal overlays (a second
 * RN Modal would fight this one on web and make the form vanish).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, Platform, ActivityIndicator } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Modal, ActionButton, Icon } from '../ui';
import Checkbox from '../checkbox';
import { web } from '../../utils/web';
import { showToast } from '../../components/toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { addResume, extractResumeFile, updateCandidate } from '../../services/hire/candidateV2';
import type { CandidateDetail } from '../../schemas/hire/candidateV2';

const RESUME_ACCEPT = '.pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export default function UpdateResumeModal({
  open, candidate, onClose, onUpdated,
}: {
  open: boolean;
  /** The full record, so the current extracted profile can be shown for
   *  comparison before it is replaced. */
  candidate: CandidateDetail | null;
  onClose: () => void;
  onUpdated: (c: CandidateDetail) => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;

  const [resumeText, setResumeText] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  // SK-Drive id of an uploaded original, so the new version points at the real
  // document instead of the 'inline' default.
  const [sourceFileId, setSourceFileId] = useState<string | null>(null);
  const [sourceFilename, setSourceFilename] = useState<string | null>(null);
  // Contact details scraped from the uploaded document. Kept separate from the
  // record's own PII: a resume naming someone else is a signal, not an instruction.
  const [scraped, setScraped] = useState<{ name: string | null; email: string | null; phone: string | null } | null>(null);
  const [applyContact, setApplyContact] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [success, setSuccess] = useState<CandidateDetail | null>(null);

  useEffect(() => {
    if (!open) return;
    setResumeText(''); setConfirmOpen(false); setSuccess(null);
    setSourceFileId(null); setSourceFilename(null); setUploading(false);
    setScraped(null); setApplyContact(false);
  }, [open]);

  /** Upload a PDF/CSV/Excel resume: the server parses it and returns the text,
   *  which fills the box below so it can be reviewed before it is committed. */
  const onUpload = () => {
    if (Platform.OS !== 'web') { showToast('error', 'Upload on web', 'Resume upload is available in the web app.'); return; }
    const doc: any = (globalThis as any).document;
    const el = doc.createElement('input');
    el.type = 'file';
    el.accept = RESUME_ACCEPT;
    el.onchange = async () => {
      const f: File | undefined = el.files?.[0];
      if (!f) return;
      setUploading(true);
      try {
        const contentBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(f);
        });
        const r = await extractResumeFile({ filename: f.name, contentBase64, contentType: f.type || 'application/octet-stream' });
        if (!r.resumeText?.trim()) throw new Error('No readable text in that file.');
        setResumeText(r.resumeText);
        setSourceFileId(r.sourceFileId);
        setSourceFilename(r.sourceFilename ?? f.name);
        setScraped({ name: r.name ?? null, email: r.email ?? null, phone: r.phone ?? null });
        setApplyContact(false); // overwriting identity is opt-in, never automatic
        showToast('success', 'Read from file', 'Review the text below, then update the resume.');
      } catch (e) {
        showToast('error', 'Could not read that file', getApiErrorMessage(e, 'Try a PDF, CSV or Excel file.'));
      } finally {
        setUploading(false);
      }
    };
    el.click();
  };

  const close = () => { if (!busy) onClose(); };

  const onSaveClick = () => {
    if (!resumeText.trim()) {
      showToast('error', 'Resume text is required', 'Paste the updated resume before saving.');
      return;
    }
    setConfirmOpen(true);
  };

  const onConfirm = async () => {
    if (!candidate) return;
    setBusy(true);
    try {
      let updated = await addResume(candidate.id, resumeText.trim(), sourceFileId);
      // Opt-in identity update. Done AFTER the version lands, so a failure here
      // cannot lose the resume the recruiter just supplied.
      if (applyContact && scraped?.name) {
        updated = await updateCandidate(candidate.id, {
          name: scraped.name.trim(),
          email: (scraped.email ?? candidate.email ?? '').trim(),
          phone: scraped.phone ?? candidate.phone ?? null,
        });
      }
      setConfirmOpen(false);
      setSuccess(updated);
    } catch (e) {
      setConfirmOpen(false);
      showToast('error', 'Could not update the resume', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onDone = () => {
    const u = success;
    setSuccess(null);
    onClose();
    if (u) onUpdated(u);
  };

  const overlayNode =
    confirmOpen ? (
      <OverlayScrim>
        <OverlayCard accent={colors.accent} shadows={shadows}>
          <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accentSoft, alignSelf: 'center' }}>
            <Icon name="upload" size={24} color={colors.ink} />
          </View>
          <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>Add a new resume version?</Text>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textAlign: 'center', lineHeight: 20 }}>
            This appends a new resume version for {candidate?.name} and re-extracts the profile (location, experience, skills). Earlier versions stay in history.
          </Text>
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 6 }}>
            <View style={{ flex: 1 }}><ActionButton label="Keep editing" variant="secondary" onPress={() => setConfirmOpen(false)} /></View>
            <View style={{ flex: 1 }}><ActionButton label="Update resume" variant="primary" loading={busy} onPress={onConfirm} /></View>
          </View>
        </OverlayCard>
      </OverlayScrim>
    ) : success ? (
      <OverlayScrim>
        <OverlayCard accent={colors.success} shadows={shadows}>
          <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.successSoft, alignSelf: 'center' }}>
            <Icon name="check-circle" size={28} color={colors.success} />
          </View>
          <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>Resume updated</Text>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textAlign: 'center', lineHeight: 20 }}>
            {candidate?.name} is now on resume v{success.profile?.resumeVersion ?? ''} — the profile was re-extracted from the new text.
          </Text>
          <View style={{ marginTop: 4 }}><ActionButton label="Done" variant="primary" onPress={onDone} /></View>
        </OverlayCard>
      </OverlayScrim>
    ) : undefined;

  return (
    <Modal
      open={open}
      onClose={close}
      title={candidate ? `Update resume — ${candidate.name}` : 'Update resume'}
      size="wide"
      overlay={overlayNode}
      footer={
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onPress={close} />
          <ActionButton label="Update resume" variant="primary" onPress={onSaveClick} loading={busy} />
        </View>
      }
    >
      <View style={{ gap: 14 }}>
        {/* What is on file today. The raw resume TEXT is deliberately not stored
            (only the extracted profile + a content hash + the original file in
            SK-Drive), so it cannot be pre-filled for editing — but the extracted
            result can be shown, which is what actually drives matching. */}
        {!!candidate?.profile && (
          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, backgroundColor: colors.paper2, overflow: 'hidden' }}>
            <View style={{ paddingHorizontal: 12, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Icon name="user" size={13} color={colors.ink3} />
              <Text style={{ flex: 1, fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Currently on file · resume v{candidate.profile.resumeVersion}
              </Text>
              {!!candidate.sourceFilename && (
                <Text numberOfLines={1} style={{ maxWidth: 220, fontSize: fontSize.xs, color: colors.ink4 }}>{candidate.sourceFilename}</Text>
              )}
            </View>
            <View style={{ padding: 12, gap: 8 }}>
              {!!candidate.profile.summaryCompressed && (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink2, lineHeight: 19 }}>{candidate.profile.summaryCompressed}</Text>
              )}
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>
                {[
                  candidate.profile.totalExperienceYears != null ? `${candidate.profile.totalExperienceYears} y experience` : '',
                  candidate.profile.currentLocation ?? '',
                  candidate.profile.noticePeriodDays != null ? `${candidate.profile.noticePeriodDays} d notice` : '',
                ].filter(Boolean).join(' · ')}
              </Text>
              {(candidate.profile.skills ?? []).length > 0 && (
                <Text style={{ fontSize: fontSize.xs, color: colors.ink3, lineHeight: 18 }}>
                  {(candidate.profile.skills ?? []).map((sk) => sk.slug).join(' · ')}
                </Text>
              )}
            </View>
          </View>
        )}

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ flex: 1, minWidth: 0, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium }}>
            New resume text
            {/* Name the version this will create, so "which one am I looking at"
                is answerable before the write, not only after. */}
            <Text style={{ color: colors.ink4, fontWeight: fontWeight.regular }}>
              {'  ·  becomes v'}{(candidate?.profile?.resumeVersion ?? 1) + 1}
            </Text>
          </Text>
          {uploading
            ? <ActivityIndicator size="small" color={colors.accent} />
            : <ActionButton label="Upload file" variant="secondary" size="sm" icon="upload" onPress={onUpload} />}
        </View>
        {/* The uploaded document names someone other than this record. That is
            almost always one of two things: a corrected/married name on the same
            person, or the wrong file on the wrong candidate. Overwriting identity
            silently would merge two people's consent + pipeline history into one
            record, so it is opt-in and the alternative is spelled out. */}
        {!!scraped?.name && candidate && scraped.name.trim().toLowerCase() !== candidate.name.trim().toLowerCase() && (
          <View style={{ borderWidth: 1, borderColor: colors.warn, borderRadius: radii.md, backgroundColor: colors.warnSoft, padding: 12, gap: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Icon name="alert-circle" size={14} color={colors.warn} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold }}>
                This resume names someone else
              </Text>
            </View>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink2, lineHeight: 17 }}>
              The file says <Text style={{ fontWeight: fontWeight.semibold }}>{scraped.name}</Text>
              {scraped.email ? ` (${scraped.email})` : ''}, but this record is{' '}
              <Text style={{ fontWeight: fontWeight.semibold }}>{candidate.name}</Text>
              {candidate.email ? ` (${candidate.email})` : ''}.
            </Text>
            <Checkbox
              checked={applyContact}
              onChange={setApplyContact}
              label={`Also rename this record to ${scraped.name}`}
            />
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, lineHeight: 17 }}>
              Tick this only if it is the same person under a different name. If it is a genuinely different
              person, cancel and add them from Candidates → Add candidate instead — otherwise both people end up
              sharing one record's consent, resume history and pipeline actions.
            </Text>
          </View>
        )}

        {!!sourceFilename && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
            <Icon name="check-circle" size={13} color={colors.success} />
            <Text numberOfLines={1} style={{ flex: 1, fontSize: fontSize.xs, color: colors.ink3 }}>
              {sourceFilename} — read and kept on file as the source of v{(candidate?.profile?.resumeVersion ?? 1) + 1}
            </Text>
          </View>
        )}
        <TextInput
          value={resumeText}
          onChangeText={setResumeText}
          placeholder="Paste the candidate's updated resume. A new version is appended and the profile is re-extracted."
          placeholderTextColor={colors.ink4}
          multiline
          style={[{ minHeight: 200, padding: 12, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, backgroundColor: colors.paper2, color: colors.ink, fontSize: fontSize.md, textAlignVertical: 'top' }, web({ outline: 'none' })]}
        />
        <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>
          The details above are replaced by whatever is extracted from this text — the earlier version stays in history.
          A byte-identical re-upload is ignored (no new version, no AI spend). PII (name, email, phone) is edited from the header “Edit” instead.
        </Text>
      </View>
    </Modal>
  );
}

function OverlayScrim({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={[
        { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: 'rgba(8,10,14,0.55)' },
        web({ animation: 'sk-rise 160ms ease both', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as any),
      ]}
    >
      {children}
    </View>
  );
}

function OverlayCard({ accent, shadows, children }: { accent: string; shadows: { lg: object }; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, radii } = theme;
  return (
    <View style={[{ width: '100%', maxWidth: 420, backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' }, shadows.lg]}>
      <View style={{ height: 3, backgroundColor: accent }} />
      <View style={{ padding: 20, gap: 12 }}>{children}</View>
    </View>
  );
}
